import { useEffect, useState } from 'react';

import { css, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';
import { Button } from 'src/libraries/design/components/button/src/button';

import { token } from '@atlaskit/tokens';

import {
	getConversionTime,
	MAX_TIMEOUT_MS,
	type WebinarMode,
} from '../../webinar-form/src/webinar-form';
import { webinarFormMessages } from '../../webinar-form/src/webinar-form-messages';

import type { UniformDateTimeValue } from 'src/uniform/utils/uniform-date-time';

const webinarButtonCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
});

const alignmentCss = cssMap({
	center: {
		alignItems: 'center',
	},
	start: {
		alignItems: 'flex-start',
	},
});

type WebinarButtonAlignment = 'center' | 'start';

const getWebinarMode = (conversionTime: number | undefined): WebinarMode => {
	if (conversionTime === undefined) {
		return 'scheduled';
	}

	return conversionTime <= Date.now() ? 'on-demand' : 'scheduled';
};

export interface UniformWebinarButtonProps {
	onDemandConversionDateTime: UniformDateTimeValue;
	fullUrlPath: string;
	alignment?: WebinarButtonAlignment;
}

const UniformWebinarButton = ({
	onDemandConversionDateTime,
	fullUrlPath,
	alignment = 'center',
}: UniformWebinarButtonProps) => {
	const intl = useIntl();
	const conversionTime = getConversionTime(onDemandConversionDateTime);
	const [mode, setMode] = useState<WebinarMode>(() => getWebinarMode(conversionTime));

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const updateMode = () => {
			const nextMode = getWebinarMode(conversionTime);
			setMode(nextMode);

			if (nextMode !== 'scheduled' || conversionTime === undefined) {
				return;
			}

			const remainingTime = conversionTime - Date.now();
			timeoutId = setTimeout(updateMode, Math.min(remainingTime, MAX_TIMEOUT_MS));
		};

		updateMode();

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [conversionTime]);

	const buttonLabel =
		mode === 'on-demand'
			? intl.formatMessage(webinarFormMessages.watchButton)
			: intl.formatMessage(webinarFormMessages.registerButton);

	return (
		<div css={[webinarButtonCss, alignmentCss[alignment]]}>
			{mode === 'scheduled' && conversionTime !== undefined && (
				<time dateTime={onDemandConversionDateTime.datetime} className="body-md">
					{intl.formatDate(conversionTime, {
						day: 'numeric',
						month: 'short',
						weekday: 'short',
						year: 'numeric',
					})}
				</time>
			)}
			<Button
				href={fullUrlPath}
				variant={mode === 'on-demand' ? 'primary' : 'primary_alt'}
				dataEventProps={{
					'data-product-key': 'uniform',
					'data-event-component': 'linkButton',
					'data-event-container': 'webinar',
					'data-event': 'clicked',
				}}
			>
				{buttonLabel}
			</Button>
		</div>
	);
};

export default UniformWebinarButton;
